import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Phone, CheckCircle, ArrowRight, Star, MapPin,
  Home, PoundSterling, Shield, Clock, Users, Train
} from 'lucide-react';

// Area data for all locations
export const areaData = {
  'harrow': {
    name: 'Harrow',
    borough: 'London Borough of Harrow',
    description: 'The heart of our operations. Harrow offers excellent transport links, diverse property types, and strong rental demand from professionals and families.',
    highlights: ['Metropolitan & Overground lines', 'Excellent schools', 'Strong rental yields', 'Diverse property stock'],
    propertyTypes: ['Victorian terraces', '1930s semis', 'Modern flats', 'Period conversions'],
    avgRent: '£1,400 - £2,200',
    tenantProfile: 'Professionals, families, students'
  },
  'pinner': {
    name: 'Pinner',
    borough: 'London Borough of Harrow',
    description: 'A leafy, affluent suburb on the Metropolitan line. Pinner attracts families and professionals seeking village charm with London connectivity.',
    highlights: ['Village atmosphere', 'Outstanding schools', 'Metropolitan line access', 'Period properties'],
    propertyTypes: ['Edwardian homes', 'Detached houses', 'Character cottages', 'Family homes'],
    avgRent: '£1,600 - £2,800',
    tenantProfile: 'Families, professionals'
  },
  'wembley': {
    name: 'Wembley',
    borough: 'London Borough of Brent',
    description: 'A vibrant, well-connected area with excellent transport and regeneration investment. Strong demand from young professionals and sharers.',
    highlights: ['Wembley Stadium proximity', 'Major regeneration', 'Fast Bakerloo/Metropolitan links', 'High rental demand'],
    propertyTypes: ['New-build flats', '1930s houses', 'HMO conversions', 'Purpose-built rentals'],
    avgRent: '£1,300 - £2,000',
    tenantProfile: 'Young professionals, sharers, key workers'
  },
  'kenton': {
    name: 'Kenton',
    borough: 'London Borough of Brent / Harrow border',
    description: 'A residential area straddling Brent and Harrow with good schools and transport. Popular with families seeking value.',
    highlights: ['Bakerloo line station', 'Good local schools', 'Quiet residential streets', 'Family-friendly'],
    propertyTypes: ['1930s semis', 'Terraced houses', 'Maisonettes', 'Family homes'],
    avgRent: '£1,400 - £2,100',
    tenantProfile: 'Families, professionals'
  },
  'north-wembley': {
    name: 'North Wembley',
    borough: 'London Borough of Brent',
    description: 'A quieter alternative to central Wembley with good transport links and more affordable rents. Growing demand from professionals.',
    highlights: ['Bakerloo line access', 'More affordable', 'Close to Wembley amenities', 'Residential feel'],
    propertyTypes: ['1930s houses', 'Converted flats', 'Terraces', 'Ex-council properties'],
    avgRent: '£1,200 - £1,800',
    tenantProfile: 'Professionals, first-time renters'
  },
  'ruislip': {
    name: 'Ruislip',
    borough: 'London Borough of Hillingdon',
    description: 'A sought-after suburban area with excellent schools, green spaces, and Metropolitan/Piccadilly line access. Premium rental market.',
    highlights: ['Ruislip Lido & woods', 'Outstanding schools', 'Two tube lines', 'Village high street'],
    propertyTypes: ['Detached homes', '1930s semis', 'Modern developments', 'Family houses'],
    avgRent: '£1,500 - £2,500',
    tenantProfile: 'Families, professionals, relocators'
  },
  'northolt': {
    name: 'Northolt',
    borough: 'London Borough of Ealing',
    description: 'An affordable West London option with Central line access. Popular with key workers and first-time renters seeking value.',
    highlights: ['Central line station', 'Affordable rents', 'Good local amenities', 'Growing investment'],
    propertyTypes: ['Ex-council houses', '1930s terraces', 'Flats', 'Family homes'],
    avgRent: '£1,100 - £1,700',
    tenantProfile: 'Key workers, young professionals, families'
  },
  'eastcote': {
    name: 'Eastcote',
    borough: 'London Borough of Hillingdon',
    description: 'A pleasant suburban area between Ruislip and Pinner. Good schools, green spaces, and Metropolitan/Piccadilly access.',
    highlights: ['Two tube lines', 'Village atmosphere', 'Good schools', 'Family-oriented'],
    propertyTypes: ['1930s semis', 'Detached homes', 'Bungalows', 'Period properties'],
    avgRent: '£1,400 - £2,300',
    tenantProfile: 'Families, commuters'
  },
  'edgware': {
    name: 'Edgware',
    borough: 'London Borough of Barnet',
    description: 'A diverse, well-connected area at the end of the Northern line. Strong demand from families and professionals.',
    highlights: ['Northern line terminus', 'Major shopping centre', 'Diverse community', 'Good bus links'],
    propertyTypes: ['1930s houses', 'New-build flats', 'Family homes', 'Conversions'],
    avgRent: '£1,300 - £2,100',
    tenantProfile: 'Families, professionals, commuters'
  },
  'burnt-oak': {
    name: 'Burnt Oak',
    borough: 'London Borough of Barnet',
    description: 'An affordable option on the Northern line with improving amenities. Popular with first-time renters and key workers.',
    highlights: ['Northern line access', 'Affordable entry point', 'Improving high street', 'Close to Edgware'],
    propertyTypes: ['Ex-council flats', 'Terraced houses', '1930s properties', 'Conversions'],
    avgRent: '£1,100 - £1,600',
    tenantProfile: 'Key workers, young professionals, sharers'
  },
  'kingsbury': {
    name: 'Kingsbury',
    borough: 'London Borough of Brent',
    description: 'A residential area with good Jubilee line links and strong community feel. Popular with families seeking space and value.',
    highlights: ['Jubilee line station', 'Fryent Country Park', 'Strong community', 'Family homes'],
    propertyTypes: ['1930s semis', 'Detached houses', 'Family homes', 'Conversions'],
    avgRent: '£1,400 - £2,200',
    tenantProfile: 'Families, professionals'
  },
  'stanmore': {
    name: 'Stanmore',
    borough: 'London Borough of Harrow',
    description: 'An affluent suburban area at the northern end of the Jubilee line. Known for excellent schools, green spaces, and premium properties.',
    highlights: ['Jubilee line terminus', 'Stanmore Country Park', 'Outstanding schools', 'Affluent community'],
    propertyTypes: ['Detached homes', 'Large family houses', 'Period properties', 'New developments'],
    avgRent: '£1,800 - £3,000',
    tenantProfile: 'Professionals, families, executives'
  },
  'queensbury': {
    name: 'Queensbury',
    borough: 'London Borough of Brent/Harrow border',
    description: 'A well-connected residential area on the Jubilee line with good amenities and transport links to central London.',
    highlights: ['Jubilee line station', 'Good local shops', 'Quiet residential streets', 'Close to Kingsbury'],
    propertyTypes: ['1930s semis', 'Terraced houses', 'Flats', 'Family homes'],
    avgRent: '£1,300 - £2,000',
    tenantProfile: 'Families, commuters, professionals'
  },
  'colindale': {
    name: 'Colindale',
    borough: 'London Borough of Barnet',
    description: 'A rapidly regenerating area on the Northern line with significant new housing developments. Popular with young professionals.',
    highlights: ['Northern line access', 'Major regeneration', 'New-build developments', 'Growing amenities'],
    propertyTypes: ['New-build flats', 'Purpose-built rentals', '1930s houses', 'Conversions'],
    avgRent: '£1,200 - £1,800',
    tenantProfile: 'Young professionals, first-time renters, sharers'
  },
  'hendon': {
    name: 'Hendon',
    borough: 'London Borough of Barnet',
    description: 'A popular North London suburb with excellent transport links, diverse community, and strong rental demand from students and professionals.',
    highlights: ['Northern line station', 'Middlesex University', 'Good schools', 'Diverse amenities'],
    propertyTypes: ['Victorian houses', '1930s semis', 'Student lets', 'Family homes'],
    avgRent: '£1,400 - £2,200',
    tenantProfile: 'Students, professionals, families'
  }
};

// Reviews to display
export const landlordReviews = [
  {
    quote: "Harry & Jacobs made managing my property completely stress-free. Their team is professional, responsive, and reliable.",
    location: "Landlord, Harrow"
  },
  {
    quote: "Excellent service from start to finish. They found tenants quickly and handle all property management tasks efficiently.",
    location: "Landlord, Harrow"
  },
  {
    quote: "I've been impressed with how smoothly everything runs. Rent collection, maintenance, and tenant communication are all handled expertly.",
    location: "Landlord, Harrow"
  },
  {
    quote: "Working with Harry & Jacobs has given me peace of mind. I know my property is in safe hands and tenants are happy.",
    location: "Landlord, Harrow"
  },
  {
    quote: "They provide clear, professional advice and always keep me informed. Managing my rental property has never been easier.",
    location: "Landlord, Harrow"
  },
  {
    quote: "Highly recommend for any landlord in Harrow. The team is thorough, trustworthy, and very efficient.",
    location: "Landlord, Harrow"
  },
  {
    quote: "From finding tenants to ongoing property management, Harry & Jacobs handle everything with professionalism and care.",
    location: "Landlord, Harrow"
  },
  {
    quote: "A reliable and proactive property management service. I've been very happy with the support and communication.",
    location: "Landlord, Harrow"
  }
];

export const AreaPage = ({ areaSlug }) => {
  const area = areaData[areaSlug] || areaData['harrow'];
  const displayReviews = landlordReviews.slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#404040] to-[#2a2a2a] text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-[#FF8C42] mb-4">
              <MapPin className="w-5 h-5" />
              <span className="font-medium">{area.borough}</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              Letting Agents in {area.name}
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              {area.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/landlord/free-valuation">
                <Button className="bg-[#FF8C42] hover:bg-[#FF6B35] text-white px-8 py-6 text-lg">
                  Get Free Valuation <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="tel:02081898889">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  <Phone className="w-5 h-5 mr-2" /> 0208 189 8889
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Area Highlights */}
      <div className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-6">
            {area.highlights.map((highlight, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm">
                <CheckCircle className="w-6 h-6 text-[#FF8C42] flex-shrink-0" />
                <span className="font-medium text-[#404040]">{highlight}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8">
            
            {/* Left Content */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-[#404040] mb-4">
                  Property Management in {area.name}
                </h2>
                <p className="text-gray-600 mb-4">
                  As local letting agents covering {area.name} and the surrounding areas, we understand the unique characteristics of this market. Whether you own a single property or a growing portfolio, our team provides comprehensive landlord services tailored to {area.name}'s rental landscape.
                </p>
                <p className="text-gray-600">
                  From tenant finding to full property management, we handle everything so you can enjoy hassle-free landlording. Our local expertise means we know what tenants are looking for in {area.name} and how to achieve the best rental returns for your property.
                </p>
              </div>

              {/* Property Types */}
              <div>
                <h3 className="text-xl font-bold text-[#404040] mb-4">
                  Property Types We Manage in {area.name}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {area.propertyTypes.map((type, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-gray-600">
                      <Home className="w-5 h-5 text-[#FF8C42]" />
                      {type}
                    </div>
                  ))}
                </div>
              </div>

              {/* Services */}
              <div>
                <h3 className="text-xl font-bold text-[#404040] mb-4">
                  Our Landlord Services in {area.name}
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <Users className="w-8 h-8 text-[#FF8C42] mb-2" />
                      <h4 className="font-bold">Tenant Finding</h4>
                      <p className="text-sm text-gray-600">Professional marketing, referencing, and viewings to find quality tenants fast.</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <Home className="w-8 h-8 text-[#FF8C42] mb-2" />
                      <h4 className="font-bold">Full Management</h4>
                      <p className="text-sm text-gray-600">Complete hands-off service including rent collection and maintenance.</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <Shield className="w-8 h-8 text-[#FF8C42] mb-2" />
                      <h4 className="font-bold">Compliance</h4>
                      <p className="text-sm text-gray-600">We handle all legal requirements - gas safety, EPCs, and licensing.</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <PoundSterling className="w-8 h-8 text-[#FF8C42] mb-2" />
                      <h4 className="font-bold">Rent Collection</h4>
                      <p className="text-sm text-gray-600">Guaranteed rent on time with professional arrears management.</p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="space-y-6">
              {/* Quick Stats Card */}
              <Card className="bg-[#FF8C42] text-white">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-4">{area.name} Rental Market</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm opacity-80">Average Monthly Rent</div>
                      <div className="text-2xl font-bold">{area.avgRent}</div>
                    </div>
                    <div>
                      <div className="text-sm opacity-80">Typical Tenants</div>
                      <div className="font-medium">{area.tenantProfile}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* CTA Card */}
              <Card className="border-2 border-[#FF8C42]">
                <CardContent className="p-6 text-center">
                  <h3 className="font-bold text-lg text-[#404040] mb-2">
                    Own a Property in {area.name}?
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Get a free rental valuation and see what your property could achieve.
                  </p>
                  <Link to="/landlord/free-valuation">
                    <Button className="w-full bg-[#FF8C42] hover:bg-[#FF6B35]">
                      Get Free Valuation
                    </Button>
                  </Link>
                  <p className="text-xs text-gray-500 mt-3">
                    Or call: <a href="tel:02081898889" className="text-[#FF8C42]">0208 189 8889</a>
                  </p>
                </CardContent>
              </Card>

              {/* Review Card */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-[#FF8C42] text-[#FF8C42]" />
                    ))}
                  </div>
                  <p className="text-gray-600 italic text-sm mb-3">
                    "{displayReviews[0].quote}"
                  </p>
                  <p className="text-[#FF8C42] font-medium text-sm">
                    — {displayReviews[0].location}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Reviews Section */}
      <div className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-[#404040] text-center mb-8">
            What Landlords Say About Us
          </h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {displayReviews.map((review, idx) => (
              <Card key={idx}>
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-[#FF8C42] text-[#FF8C42]" />
                    ))}
                  </div>
                  <p className="text-gray-600 italic mb-3">"{review.quote}"</p>
                  <p className="text-[#FF8C42] font-medium text-sm">— {review.location}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Other Areas */}
      <div className="py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-[#404040] text-center mb-6">
            We Also Cover
          </h2>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {Object.entries(areaData)
              .filter(([slug]) => slug !== areaSlug)
              .map(([slug, data]) => (
                <Link 
                  key={slug} 
                  to={`/areas/${slug}`}
                  className="bg-gray-100 hover:bg-[#FF8C42] hover:text-white text-[#404040] px-4 py-2 rounded-full text-sm font-medium transition-colors"
                >
                  {data.name}
                </Link>
              ))}
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="bg-[#FF8C42] py-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Let Your {area.name} Property?
          </h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            Get a free, no-obligation rental valuation from Harrow's trusted letting experts.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/landlord/free-valuation">
              <Button className="bg-white text-[#FF8C42] hover:bg-gray-100 px-8 py-6 text-lg font-semibold">
                Get Free Valuation <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="tel:02081898889">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                <Phone className="w-5 h-5 mr-2" /> 0208 189 8889
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AreaPage;
